import clsx from 'clsx'
import type { ReactNode } from 'react'
import { ScrollView, View, type ScrollViewProps } from 'react-native'
import type { ViewProps } from 'react-native-svg/lib/typescript/fabric/utils'

import { Placeholder } from '@/src/elements/placeholder'
import { Typography } from '@/src/elements/text/Typography'
import { CommunityCard } from '@/src/features/community/ui/components/CommunityCard/CommunityCard'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import type { TEventOriginParam, TSourceParam } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'
import { useHorizontalWheelScroll } from '@/src/general/hooks/useHorizontalWheelScroll'

import { useCommunitiesQuery } from '../../services/useCommunitiesQuery'
import { useSuggestionsVisibility } from '../../services/useSuggestionsVisibility'

type TCommunitiesSectionProps = TSourceParam &
  TEventOriginParam & {
    sectionTitle?: string
    titleProps?: ViewProps
    scrollViewProps?: ScrollViewProps
    containerProps?: ViewProps
    error?: Error | null
  }

export const SuggestedCommunitiesSection = ({
  sectionTitle,
  titleProps: { className: titleClassName, ...titleProps } = {},
  scrollViewProps,
  containerProps: { className: scrollViewContainerClassName, ...containerProps } = {},
  source,
  origin,
  error,
}: TCommunitiesSectionProps): ReactNode => {
  const { data } = useCommunitiesQuery({ fields: ['hasJoined'] })
  const visibilityProps = useSuggestionsVisibility()
  const { scrollViewRef, enableScroll } = useHorizontalWheelScroll()

  if (error) return <ErrorMessage defaultErrorKey="community.load.error" error={error} />

  return (
    <>
      {sectionTitle && (
        <View className={clsx('mt-8 rounded-md px-6 md:px-9', titleClassName)} {...titleProps}>
          <Typography size="lg" colors="default" contrast="low" heading="h2" className="text-left font-black">
            {sectionTitle}
          </Typography>
        </View>
      )}
      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        pagingEnabled
        onPointerEnter={enableScroll}
        testID={TEST_ID.PRAYER_SPACE.COMMUNITY_SUGGESTIONS_SECTION}
        {...scrollViewProps}
      >
        <View
          className={clsx(
            'mb-4 flex flex-row gap-2.5 px-2 py-4 md:max-w-screen-md md:px-5',
            scrollViewContainerClassName,
          )}
          {...containerProps}
          {...visibilityProps}
        >
          {data
            ? data.map((item) => <CommunityCard key={item.id} community={item} source={source} origin={origin} />)
            : // oxlint-disable-next-line react/no-array-index-key
              Array.from({ length: 8 }).map((_, index) => <Placeholder key={index} className="h-[396px] w-[240px]" />)}
        </View>
      </ScrollView>
    </>
  )
}
